import { useEffect, useState } from 'react';
import {
    collection,
    doc,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    deleteDoc,
    getCountFromServer,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';

export const useAgendas = () => {
    const { user } = useContext(AuthContext);
    const [agendas, setAgendas] = useState([]);
    const [counts, setCounts] = useState({});
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!user) {
            setAgendas([]);
            setCounts({});
            return;
        }

        const colRef = collection(db, 'users', user.uid, 'agendas');
        const q = query(colRef, orderBy('createdAt', 'asc'));

        const unsubscribe = onSnapshot(
            q,
            async (snapshot) => {
                let data = snapshot.docs.map((docSnap) => ({
                    id: docSnap.id,
                    ...docSnap.data(),
                }));
                // Ensure 'main' and 'manual' agendas exist
                const hasMain = data.some(a => a.id === 'main');
                const hasManual = data.some(a => a.id === 'manual');
                if (!hasMain) {
                    data.unshift({ id: 'main', name: 'Nuevos contactos sin clasificar', createdAt: new Date() });
                }
                if (!hasManual) {
                    data.push({ id: 'manual', name: 'Clientes creados manualmente', createdAt: new Date() });
                    // Create the document if not exists
                    try {
                        const manualDoc = doc(db, 'users', user.uid, 'agendas', 'manual');
                        await setDoc(manualDoc, {
                            name: 'Clientes creados manualmente',
                            createdAt: serverTimestamp(),
                        }, { merge: true });
                    } catch (err) {
                        }
                }
                setAgendas(data);

                // Get counts
                const newCounts = {};
                for (const agenda of data) {
                    try {
                        let countRef;
                        if (agenda.id === 'main') {
                            countRef = collection(db, 'users', user.uid, 'agenda');
                        } else {
                            countRef = collection(db, 'users', user.uid, 'agendas', agenda.id, 'contacts');
                        }
                        const snapshot = await getCountFromServer(countRef);
                        newCounts[agenda.id] = snapshot.data().count;
                    } catch (err) {
                        newCounts[agenda.id] = 0;
                    }
                }
                setCounts(newCounts);
            },
            (err) => {
                setError(err);
            }
        );

        return () => unsubscribe();
    }, [user]);

    const updateCounts = async () => {
        if (!user || agendas.length === 0) return;

        const newCounts = {};
        for (const agenda of agendas) {
            try {
                let countRef;
                if (agenda.id === 'main') {
                    countRef = collection(db, 'users', user.uid, 'agenda');
                } else {
                    countRef = collection(db, 'users', user.uid, 'agendas', agenda.id, 'contacts');
                }
                const snapshot = await getCountFromServer(countRef);
                newCounts[agenda.id] = snapshot.data().count;
            } catch (err) {
                newCounts[agenda.id] = 0;
            }
        }
        setCounts(newCounts);
    };

    const createAgenda = async (name) => {
        if (!user || !name.trim()) return;
        try {
            const agendaId = `agenda_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const docRef = doc(db, 'users', user.uid, 'agendas', agendaId);
            await setDoc(docRef, {
                name: name.trim(),
                createdAt: serverTimestamp(),
            });
            return agendaId;
        } catch (err) {
            throw err;
        }
    };

    const updateAgendaName = async (agendaId, name) => {
        if (!user || !agendaId || !name.trim()) return;
        try {
            const docRef = doc(db, 'users', user.uid, 'agendas', agendaId);
            await updateDoc(docRef, {
                name: name.trim(),
                updatedAt: serverTimestamp(),
            });
        } catch (err) {
            }
    };

    const deleteAgenda = async (agendaId) => {
        if (!user || !agendaId || agendaId === 'main' || agendaId === 'manual') return;
        try {
            // First, move all contacts to main
            // This would require querying contacts, but for simplicity, assume handled elsewhere
            const docRef = doc(db, 'users', user.uid, 'agendas', agendaId);
            await deleteDoc(docRef);
        } catch (err) {
            }
    };

    return {
        agendas,
        counts,
        error,
        createAgenda,
        updateAgendaName,
        deleteAgenda,
        updateCounts,
    };
};
